import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Kanban, Warning, Clock, ShieldWarning, Buildings, Archive, CaretRight, CaretDown, ShareNetwork, FilePdf, ArrowCounterClockwise, ArchiveTrayIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canManagePlanner } from '@/auth/AuthContext';
import { DashboardZone, AllClear, MetricsDisclosure } from '@/components/enterprise/DashboardZone';
import { plannerApi } from './plannerApi';
import { PRIORITY_BAR, ganttOrder } from './plannerConstants';
import AlertList from './components/AlertList';
import ProjectCard from './components/ProjectCard';
import GanttChart from './components/GanttChart';
import ProjectFormDialog from './components/ProjectFormDialog';
import ShareGanttDialog from './components/ShareGanttDialog';
import { ArchivedContextCard } from './components/ArchivedContextSection';
import { printGanttChart } from './ganttPrint';

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-zinc-200 p-3" data-testid={testid}>
      <p className="text-[11px] text-zinc-500">{label}</p>
      <p className="text-lg font-bold text-zinc-900 tabular-nums mt-0.5">{value}</p>
    </div>
  );
}

export default function DepartmentDashboard() {
  const { deptId } = useParams();
  const { t } = useT();
  const navigate = useNavigate();
  const ctx = useOutletContext();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [showArchivedCards, setShowArchivedCards] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [archivedCtx, setArchivedCtx] = useState([]);

  const load = useCallback(() => {
    plannerApi.dashboard({ department_id: deptId }).then(setData).catch(() => setData(null));
  }, [deptId]);
  useEffect(() => { load(); plannerApi.people().then(setPeople).catch(() => {}); }, [load]);
  useEffect(() => {
    if (includeArchived) plannerApi.archivedByDept(deptId).then((d) => setArchivedCtx(d.records || [])).catch(() => setArchivedCtx([]));
  }, [includeArchived, deptId]);

  if (!data) return <div className="py-24 text-center text-sm text-zinc-400" data-testid="planner-dept-loading">…</div>;
  const s = data.stats;

  // Finished is ONE stored field, resolved server-side by
  // services/project_lifecycle.py and returned as `is_finished`. The old
  // client-side rule (past end_date AND all tasks done) derived a lifecycle
  // fact from task ratios and a machine-managed end_date, and disagreed with
  // the daily-report picker. `is_overdue` is a SEPARATE fact; the header that
  // used to conflate finished with past-due has been replaced (see
  // planner.dept.archivedHelp) and 期限超過 is now its own marker.
  const isArchived = (p) => p.is_finished === true;
  const activeProjects = (data.gantt || []).filter((p) => !isArchived(p));
  const archivedProjects = (data.gantt || []).filter(isArchived);
  const toGanttItem = (p) => ({
    id: p.id, label: p.name, sub: t('planner.gantt.tasks', { done: p.done_count, total: p.task_count }),
    overdue: p.is_overdue === true, archivedAt: p.archived_at,
    start: p.start_date, end: p.end_date, color: PRIORITY_BAR[p.priority] || '#4F46E5', priority: p.priority,
  });
  const ganttItems = activeProjects.map(toGanttItem);
  const archivedGanttItems = archivedProjects.map(toGanttItem);
  // 一覧もガントと同じ順序（優先度 → 終了日）に揃える。既定はアーカイブ済み（完了）を非表示。
  const allSorted = [...(data.projects || [])].sort(ganttOrder((p) => p.end_date, (p) => p.start_date));
  const archivedCount = allSorted.filter(isArchived).length;
  const sortedProjects = showArchivedCards ? allSorted : allSorted.filter((p) => !isArchived(p));

  // 完了済みプロジェクト欄の「復元」— ボードを開かずに進行中へ戻す
  const restoreProject = async (it) => {
    if (!window.confirm(t('planner.dept.confirmRestore', { name: it.label }))) return;
    try {
      await plannerApi.archiveProject(it.id, { archived: false });
      toast.success(t('planner.project.restored'));
      load();
      ctx?.reloadSidebar?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('planner.error'));
    }
  };

  // 部署ガントを A4 横向けPDFとして印刷（QRコード付き・完了案件は含めない）
  const exportPdf = async () => {
    const w = await printGanttChart({
      title: `${data.department?.name || ''} ${t('planner.dept.gantt')}`,
      meta: `${t('planner.stat.activeProjects')}: ${s.active_projects} ・ ${t('planner.stat.totalTasks')}: ${s.total_tasks}`,
      ganttHeading: t('planner.dept.gantt'),
      items: ganttItems,
      emptyText: t('planner.dept.ganttEmpty'),
      itemLabel: t('planner.myTasks.project'),
      shareKind: 'department',
      shareId: deptId,
    });
    if (!w) toast.error(t('planner.popupBlocked'));
  };

  return (
    <div className="space-y-6" data-testid="planner-dept-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zinc-100 text-zinc-600 text-xs font-medium mb-1.5">
            <Buildings size={13} weight="duotone" /> {data.department?.name}
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('planner.dept.title')}</h1>
        </div>
        {canManagePlanner(user) && (
          <Button onClick={() => setFormOpen(true)} data-testid="planner-new-project-btn" className="bg-teal-700 hover:bg-teal-800">
            <Plus size={18} weight="bold" className="mr-1" /> {t('planner.project.new')}
          </Button>
        )}
      </div>

      <DashboardZone title={t('planner.zone.action')} hint={t('planner.zone.actionHint')} testid="planner-action-zone">
        {(s.overdue || 0) === 0 && (s.due_soon || 0) === 0 && (s.blocked || 0) === 0 ? (
          <AllClear message={t('planner.zone.allClear')} testid="planner-allclear" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* B4 — 6件で切っていた一覧をやめる。全件スクロール＋「すべて見る」で一覧画面へ。 */}
            <AlertList testid="dept-overdue" cardTestId="planner-overdue-card" countTestId="planner-stat-overdue"
              title={t('planner.alerts.overdue')} Icon={Warning} color="text-rose-500"
              items={data.overdue || []}
              allHref={`/planner/my-tasks?scope=all&filter=overdue&department_id=${deptId}`} />
            <AlertList testid="dept-duesoon" cardTestId="planner-duesoon-card" countTestId="planner-stat-duesoon"
              title={t('planner.alerts.dueSoon')} Icon={Clock} color="text-amber-500" dueClass="text-amber-600"
              items={data.due_soon || []}
              allHref={`/planner/my-tasks?scope=all&filter=due_soon&department_id=${deptId}`} />
          </div>
        )}
        {(s.blocked || 0) > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5" data-testid="planner-stat-blocked">
            <ShieldWarning size={16} weight="duotone" className="text-rose-500 shrink-0" />
            <p className="text-sm text-zinc-700">{t('planner.zone.blockedCount', { n: s.blocked })}</p>
          </div>
        )}
      </DashboardZone>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-base font-bold text-zinc-800">{t('planner.dept.gantt')}</h2>
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            data-testid="planner-dept-gantt-share-btn"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-indigo-200 bg-white text-[11px] font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
          >
            <ShareNetwork size={12} /> {t('planner.share')}
          </button>
          <button
            type="button"
            onClick={exportPdf}
            data-testid="planner-dept-gantt-print-btn"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-zinc-200 bg-white text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 transition-colors"
          >
            <FilePdf size={12} /> {t('planner.printPdf')}
          </button>
        </div>
        <GanttChart items={ganttItems} onItemClick={(it) => navigate(`/planner/project/${it.id}`)} emptyText={t('planner.dept.ganttEmpty')} />
        {archivedGanttItems.length > 0 && (
          <div className="mt-3 border border-zinc-200 rounded-xl bg-white overflow-hidden" data-testid="planner-dept-archive">
            <button
              onClick={() => setArchiveOpen((v) => !v)}
              aria-expanded={archiveOpen}
              className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-zinc-50 text-left transition-colors"
              data-testid="planner-dept-archive-toggle"
            >
              {archiveOpen
                ? <CaretDown size={14} weight="bold" className="text-zinc-500 shrink-0" />
                : <CaretRight size={14} weight="bold" className="text-zinc-500 shrink-0" />}
              <Archive size={16} weight="duotone" className="text-zinc-500 shrink-0" />
              <span className="text-sm font-semibold text-zinc-700">{t('planner.dept.archivedTitle')}</span>
              <Badge variant="outline" className="ml-0.5 bg-zinc-50 text-zinc-600 border-zinc-200 text-[11px] px-1.5 py-0" data-testid="planner-dept-archive-count">{archivedGanttItems.length}</Badge>
              <span className="ml-1.5 text-[11px] text-zinc-400 hidden sm:inline">{t('planner.dept.archivedHelp')}</span>
            </button>
            {archiveOpen && (
              <div className="px-3 pb-3 pt-1 border-t border-zinc-200 bg-zinc-50/40" data-testid="planner-dept-archive-body">
                <GanttChart
                  items={archivedGanttItems}
                  onItemClick={(it) => navigate(`/planner/project/${it.id}`)}
                  emptyText={t('planner.dept.archivedEmpty')}
                  rowAction={(it) => (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); restoreProject(it); }}
                      title={t('planner.project.restoreHint')}
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                      data-testid={`planner-restore-${it.id}`}
                    >
                      <ArrowCounterClockwise size={10} weight="bold" /> {t('planner.project.restore')}
                    </button>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-base font-semibold text-zinc-900">{t('planner.dept.projects')}</h2>
          <button
            type="button"
            onClick={() => setIncludeArchived((v) => !v)}
            data-testid="planner-dept-include-archived-ctx"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
              includeArchived ? 'bg-amber-600 text-white border-amber-600' : 'bg-white border-amber-200 text-amber-700 hover:bg-amber-50'
            }`}
          >
            <ArchiveTrayIcon size={12} weight={includeArchived ? 'fill' : 'regular'} />
            {t('planner.archive.includeArchived')}
          </button>
          {archivedCount > 0 && (
            <button
              type="button"
              onClick={() => setShowArchivedCards((v) => !v)}
              data-testid="planner-dept-toggle-archived-cards"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
                showArchivedCards ? 'bg-zinc-800 text-white border-zinc-800' : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50'
              }`}
            >
              <Archive size={12} weight={showArchivedCards ? 'fill' : 'regular'} />
              {t('planner.dept.showArchivedCards')} ({archivedCount})
            </button>
          )}
        </div>
        {sortedProjects.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-zinc-300 p-10 text-center">
            <Kanban size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">{t('planner.dept.noProjects')}</p>
            {canManagePlanner(user) && <Button onClick={() => setFormOpen(true)} variant="outline" className="mt-3 border-indigo-200 text-indigo-600 hover:bg-indigo-50"><Plus size={16} className="mr-1" />{t('planner.project.new')}</Button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            {sortedProjects.map((p) => (
              <ProjectCard key={p.id} project={p} onOpen={() => navigate(`/planner/project/${p.id}`)}
                testid={`planner-project-card-${p.id}`} statusTestId={`planner-dept-project-status-${p.id}`} />
            ))}
          </div>
        )}
      </div>

      {includeArchived && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-4" data-testid="planner-dept-archived-ctx-section">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ArchiveTrayIcon size={17} weight="duotone" className="text-amber-600" />
            <h3 className="text-sm font-bold text-zinc-800">{t('planner.archive.title')}</h3>
            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] px-1.5 py-0">{t('planner.archive.referenceOnly')}</Badge>
          </div>
          {archivedCtx.length === 0
            ? <p className="text-xs text-zinc-400 py-2 text-center" data-testid="planner-dept-archived-ctx-empty">{t('planner.archive.noRecords')}</p>
            : <div className="space-y-2">
                {archivedCtx.map((rec) => (
                  <div key={rec.id}>
                    {rec.candidate_project_name && (
                      <button onClick={() => navigate(`/planner/project/${rec.candidate_current_project_id}`)}
                        className="text-[11px] font-medium text-indigo-600 hover:text-indigo-700 mb-0.5">→ {rec.candidate_project_name}</button>
                    )}
                    <ArchivedContextCard rec={rec} t={t} />
                  </div>
                ))}
              </div>}
        </div>
      )}

      <MetricsDisclosure title={t('planner.metrics')} testid="planner-metrics">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
          <Stat label={t('planner.stat.activeProjects')} value={s.active_projects} testid="planner-stat-active" />
          <Stat label={t('planner.stat.totalTasks')} value={s.total_tasks} testid="planner-stat-tasks" />
        </div>
      </MetricsDisclosure>

      <ProjectFormDialog open={formOpen} onClose={() => setFormOpen(false)} departmentId={deptId} people={people}
        onSaved={() => { load(); ctx?.reloadSidebar?.(); }} />
      <ShareGanttDialog open={shareOpen} onClose={() => setShareOpen(false)} kind="department" targetId={deptId} title={data.department?.name} />
    </div>
  );
}
